import { Component, HostListener, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { finalize, take } from 'rxjs';
import { NotificationService } from '@core/notifications';
import { AuthService } from '@services/auth';
import { PermissionService } from '@services/permission.service';
import { ADMIN_NAV_SECTIONS, AdminNavSection } from './admin-nav.config';
import { AdminBookingService } from '../../services/admin/booking.service';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './admin-layout.component.html',
  styleUrl: './admin-layout.component.scss',
})
export class AdminLayoutComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);
  private readonly notify = inject(NotificationService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly adminBookings = inject(AdminBookingService);

  userName = 'Clara Reis';
  userRole = 'General Manager';
  userInitials = 'CR';
  isLoggingOut = false;
  readonly profileMenuOpen = signal(false);
  readonly navSections = signal<AdminNavSection[]>([]);

  private readonly iconCache = new Map<string, SafeHtml>();

  constructor() {
    const user = this.authService.getCurrentUser();
    if (user?.name) {
      this.userName = user.name;
      this.userInitials = user.name
        .split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    }

    if (user?.roles?.length) {
      this.userRole = user.roles[0];
    } else if (this.permissionService.roles.length) {
      this.userRole = this.permissionService.roles[0];
    }

    this.adminBookings
      .list({ per_page: '1', page: '1', status: 'all', channel: 'all' })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          const total = res?.data?.filter_counts?.status?.['all'];
          this.setNavBadge('Bookings', typeof total === 'number' ? total : undefined);
        },
        error: () => {
          this.setNavBadge('Bookings', undefined);
        },
      });
  }

  async ngOnInit(): Promise<void> {
    await this.permissionService.getPermissions();

    if (this.permissionService.roles.length) {
      this.userRole = this.permissionService.roles[0];
    }

    this.filterMenu();
  }

  navIcon(icon: string): SafeHtml {
    const cached = this.iconCache.get(icon);
    if (cached) {
      return cached;
    }

    const safe = this.sanitizer.bypassSecurityTrustHtml(icon);
    this.iconCache.set(icon, safe);
    return safe;
  }

  private filterMenu(): void {
    const sections = this.cloneNavSections(ADMIN_NAV_SECTIONS)
      .map((section) => ({
        ...section,
        links: section.links.filter((link) => this.permissionService.hasPermission(link.permission)),
      }))
      .filter((section) => section.links.length > 0);

    this.navSections.set(sections);
  }

  private cloneNavSections(sections: AdminNavSection[]): AdminNavSection[] {
    return sections.map((section) => ({
      ...section,
      links: section.links.map((link) => ({ ...link })),
    }));
  }

  private setNavBadge(label: string, badge: number | undefined): void {
    const sections = this.cloneNavSections(this.navSections());

    for (const section of sections) {
      const link = section.links.find((l) => l.label === label);
      if (link) {
        link.badge = badge;
        break;
      }
    }

    this.navSections.set(sections);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeProfileMenu();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target;

    if (!(target instanceof Node)) {
      return;
    }

    if (!this.profileMenuOpen()) {
      return;
    }

    const menuRoot = document.getElementById('admin-profile-menu');

    if (menuRoot && !menuRoot.contains(target)) {
      this.closeProfileMenu();
    }
  }

  toggleProfileMenu(): void {
    this.profileMenuOpen.update((open) => !open);
  }

  closeProfileMenu(): void {
    this.profileMenuOpen.set(false);
  }

  async onLogoutClick(): Promise<void> {
    if (this.isLoggingOut) {
      return;
    }

    this.closeProfileMenu();

    const confirmed = await this.notify.confirm({
      title: 'Sign out',
      message: 'Are you sure you want to sign out of the admin panel?',
      confirmText: 'Sign out',
      cancelText: 'Cancel',
    });

    if (!confirmed) {
      return;
    }

    this.isLoggingOut = true;

    this.authService
      .logout()
      .pipe(finalize(() => (this.isLoggingOut = false)))
      .subscribe({
        next: () => {
          void this.router.navigate(['/admin-login']);
        },
      });
  }
}
